import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, ArrowRight, Zap, Star, Crown, Phone, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const Pricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or annual

  const plans = [
    {
      name: 'Starter',
      icon: Zap,
      tagline: 'Perfect for solo agents',
      monthlyPrice: 99,
      annualPrice: 79, // $948/year (save $240)
      setupFee: 0,
      trial: '7-Day Free Trial',
      features: [
        'Up to 100 leads/month',
        '24/7 AI voice calling',
        'AI lead scoring & qualification',
        'Follow Up Boss integration',
        'SMS & Email automation',
        'Basic analytics dashboard',
        'Email support'
      ],
      cta: 'Start 7-Day Free Trial',
      popular: false,
      stripeLink: 'https://buy.stripe.com/starter-plan', // Replace with actual Stripe link
      color: 'blue'
    },
    {
      name: 'Professional',
      icon: Star,
      tagline: 'For growing teams',
      monthlyPrice: 299,
      annualPrice: 249, // $2,988/year (save $600)
      setupFee: 99,
      trial: '14-Day Free Trial',
      features: [
        'Unlimited leads',
        'Everything in Starter',
        'Advanced AI analytics',
        'Custom AI voice prompts',
        'Priority phone & email support',
        'Google Calendar sync',
        'Property matching automation',
        'Custom reporting',
        'Dedicated account manager'
      ],
      cta: 'Get Started',
      popular: true,
      stripeLink: 'https://buy.stripe.com/professional-plan', // Replace with actual Stripe link
      color: 'purple'
    },
    {
      name: 'Enterprise',
      icon: Crown,
      tagline: 'For large brokerages',
      monthlyPrice: null,
      annualPrice: null,
      setupFee: 0,
      trial: 'Custom Demo',
      features: [
        'Everything in Professional',
        'Custom AI training',
        'Multi-agent management',
        'White-label options',
        'Advanced API access',
        'Custom integrations',
        'SLA guarantees',
        'Dedicated success team',
        '24/7 priority support'
      ],
      cta: 'Schedule Demo',
      popular: false,
      calendlyLink: 'https://calendly.com/propflo/enterprise-demo', // Replace with actual Calendly link
      color: 'gold'
    }
  ];

  const handlePlanSelection = (plan) => {
    if (plan.name === 'Enterprise') {
      // Open Calendly for Enterprise demos
      if (plan.calendlyLink) {
        window.open(plan.calendlyLink, '_blank');
      } else {
        toast.info('Opening scheduling page...');
        // Fallback: Navigate to contact or use inline Calendly widget
        navigate('/contact');
      }
    } else {
      // Redirect to Stripe checkout
      if (plan.stripeLink) {
        window.location.href = plan.stripeLink;
      } else {
        toast.info('Redirecting to checkout...');
        // Fallback: Navigate to signup with plan selected
        navigate('/login', { state: { selectedPlan: plan.name } });
      }
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return {
          gradient: 'from-blue-500 to-cyan-500',
          border: 'border-blue-500/30',
          text: 'text-blue-400',
          bg: 'bg-blue-500/10'
        };
      case 'purple':
        return {
          gradient: 'from-purple-500 to-pink-500',
          border: 'border-purple-500/30',
          text: 'text-purple-400',
          bg: 'bg-purple-500/10'
        };
      case 'gold':
        return {
          gradient: 'from-yellow-500 to-orange-500',
          border: 'border-yellow-500/30',
          text: 'text-yellow-400',
          bg: 'bg-yellow-500/10'
        };
      default:
        return {
          gradient: 'from-blue-500 to-purple-500',
          border: 'border-blue-500/30',
          text: 'text-blue-400',
          bg: 'bg-blue-500/10'
        };
    }
  };

  const calculateSavings = (plan) => {
    if (!plan.annualPrice) return null;
    const monthlyCost = plan.monthlyPrice * 12;
    const annualCost = plan.annualPrice * 12;
    return monthlyCost - annualCost;
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Background Effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 px-8 py-6 flex items-center justify-between border-b border-gray-800">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="/propflo-logo.png" 
            alt="PropFlo" 
            className="h-32 w-auto"
          />
        </div>
        <div className="flex items-center gap-8">
          <a href="/#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
          <button onClick={() => navigate('/pricing')} className="text-white font-semibold">Pricing</button>
          <a href="/#testimonials" className="text-gray-300 hover:text-white transition-colors">Testimonials</a>
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="text-white hover:text-gray-200"
          >
            Sign In
          </Button>
          <Button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-8 pt-24 pb-16 text-center">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Simple, Transparent Pricing
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Choose the perfect plan for your real estate business
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-base ${billingCycle === 'monthly' ? 'text-white font-semibold' : 'text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-14 h-8 bg-gray-700 rounded-full transition-all duration-300 hover:bg-gray-600"
            >
              <div
                className={`absolute top-1 left-1 w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-transform duration-300 ${
                  billingCycle === 'annual' ? 'transform translate-x-6' : ''
                }`}
              />
            </button>
            <span className={`text-base ${billingCycle === 'annual' ? 'text-white font-semibold' : 'text-gray-400'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="ml-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-sm font-semibold">
                Save up to $600/year
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="relative z-10 px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const colors = getColorClasses(plan.color);
              const Icon = plan.icon;
              const currentPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
              const savings = calculateSavings(plan);

              return (
                <Card
                  key={index}
                  className={`relative bg-black/40 backdrop-blur-xl p-8 overflow-hidden transition-all duration-500 hover:scale-105 cursor-pointer ${
                    plan.popular
                      ? 'border-2 border-purple-500/50 shadow-2xl shadow-purple-500/20'
                      : `border ${colors.border}`
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  {/* Glowing background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-50 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Plan Name */}
                    <h3 className="text-3xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-300 text-sm mb-6">{plan.tagline}</p>

                    {/* Price */}
                    <div className="mb-6">
                      {currentPrice ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-bold text-white">${currentPrice}</span>
                            <span className="text-gray-400">/month</span>
                          </div>
                          {billingCycle === 'annual' && savings && (
                            <p className="text-green-400 text-sm mt-2">
                              Save ${savings}/year
                            </p>
                          )}
                          {plan.setupFee > 0 && (
                            <p className="text-gray-400 text-sm mt-2">
                              + ${plan.setupFee} one-time setup fee
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="text-3xl font-bold text-white">Custom Pricing</div>
                      )}
                      <p className={`${colors.text} text-sm font-semibold mt-2`}>{plan.trial}</p>
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handlePlanSelection(plan)}
                      className={`w-full mb-8 bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white font-bold py-6 text-lg shadow-lg transition-all duration-300 hover:scale-105`}
                    >
                      {plan.cta}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>

                    {/* Features List */}
                    <ul className="space-y-4">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-200 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 px-8 py-24 bg-gradient-to-b from-gray-900/50 to-gray-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-white">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time with no cancellation fees. Your service will continue until the end of your billing period.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, Mastercard, American Express) and ACH bank transfers for annual plans.'
              },
              {
                q: 'Is there a setup fee?',
                a: 'Starter plan has no setup fee. Professional plan includes a one-time $99 setup fee for custom configuration. Enterprise pricing is custom.'
              },
              {
                q: 'Do you offer refunds?',
                a: 'We offer a 7-day money-back guarantee for Starter plans and 14-day for Professional plans. If you\'re not satisfied, we\'ll refund your payment.'
              },
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Absolutely! You can upgrade or downgrade at any time. Changes take effect at the start of your next billing cycle.'
              },
              {
                q: 'What integrations are included?',
                a: 'All plans include Follow Up Boss integration. Professional and Enterprise plans include Google Calendar, custom CRM integrations, and API access.'
              }
            ].map((faq, index) => (
              <Card key={index} className="bg-black/40 border border-white/10 backdrop-blur-xl p-6">
                <h3 className="text-xl font-bold text-white mb-3">{faq.q}</h3>
                <p className="text-gray-200 leading-relaxed">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 px-8 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users, value: '500+', label: 'Active Agents' },
              { icon: Phone, value: '50K+', label: 'AI Calls Made' },
              { icon: TrendingUp, value: '60%', label: 'Avg Conversion Increase' },
              { icon: Star, value: '4.9/5', label: 'Customer Rating' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="flex flex-col items-center">
                  <Icon className="w-12 h-12 text-blue-400 mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-gray-300">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-8 py-24">
        <Card className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 backdrop-blur-xl p-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Ready to Transform Your Lead Generation?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Start your free trial today. No credit card required.
          </p>
          <Button
            onClick={() => handlePlanSelection(plans[0])}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-12 py-6 text-lg text-white shadow-lg shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transition-all duration-300"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Card>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-8 py-12 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-gray-300">
              <p>© 2025 PropFlo. Built with Emergent AI Platform.</p>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <button onClick={() => navigate('/privacy')} className="text-gray-300 hover:text-blue-400 transition-colors">
                Privacy Policy
              </button>
              <span className="text-gray-600">•</span>
              <button onClick={() => navigate('/terms')} className="text-gray-300 hover:text-blue-400 transition-colors">
                Terms of Service
              </button>
              <span className="text-gray-600">•</span>
              <a href="mailto:support@aipropflo.com" className="text-gray-300 hover:text-blue-400 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
